import React, { useContext, useMemo } from 'react';
// Hooks
import { usePollReviewComments } from '../Hooks/usePollReviewComments';

// Others
import { ReviewViewContext } from '../Context/reviewViewContext';

// UI
import Select from '@cube3/cubicle/src/core/atoms/Select/Select';
import { ReviewComment } from './ReviewComment';
import { Typography } from '@cube3/cubicle/src/core/atoms/Typography/Typography';
import { Comment } from '@cube3/state/types';
import { Scrollbar } from '@cube3/cubicle/src/core/atoms/Scrollbar';
import { Asset } from '@cube3/common/model/schema/resources/asset';
import { ReviewCommentsFilterContext } from '../Context/reviewCommentsFilterContext';
import { Option } from '@cube3/cubicle/src/core/atoms/Select/Option';
import { userActivationContext } from '../../Workspace/contexts/UserActivationContext';
import { makeCSS } from '@cube3/cubicle/src/utils/makeCSS';


const commentFilters = [
  {
    id: 'all',
    value: 'All comments'
  },
  {
    id: 'resolve',
    value: 'Resolved comments'
  },
  {
    id: 'unresolve',
    value: 'Unresolved comments'
  }
] as const;

const getFilterDisplayName = (id: string) =>
  commentFilters.find((c) => c.id === id)?.value;

export const ReviewCommentsList = React.memo(() => {

  const classes = useCSS();

  const { filter, setFilter } = useContext(ReviewCommentsFilterContext);
  const isUserActive = useContext(userActivationContext);

  const { activeAsset } = useContext(ReviewViewContext);
  const { threads, resolvedComments, unresolvedComments } =
    activeAsset && usePollReviewComments(activeAsset.id, isUserActive);





  const filteredComments = useMemo(() => {
    if (!threads) return [];
    let filtered = threads;
    switch (filter) {
      case 'unresolve':
        filtered = unresolvedComments;
        break;
      case 'resolve':
        filtered = resolvedComments;
        break;
      default:
        break;
    }
    const sorted = filtered.sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
    return sorted;
  }, [threads, filter, resolvedComments, unresolvedComments]);

  return (
    <>
      {/** Comment filters */}
      <div
        style={{
          height: 40,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap'
        }}
        data-pw="review-comment-filters"
      >
        <Select
          placeholder={'Comments view filters'}
          value={getFilterDisplayName(filter)}
        >
          {commentFilters.map((f) => {
            return (
              <Option
                key={f.id}
                value={f.value}
                onClick={() => setFilter(f.id)}
              />
            );
          })}
        </Select>
      </div>
      {/* Comments list */}
      <Comments
        comments={filteredComments}
        activeAsset={activeAsset}
        filter={filter}
      />
    </>
  );
});

interface CommentsProps {
  comments: Comment[];
  activeAsset: Asset;
  filter: 'all' | 'resolve' | 'unresolve';
}

const useCSS = makeCSS(({ theme }) => {
  return {
    scrollbarContainer: {
      marginRight: '-12px',
    }
  };
});

const Comments = React.memo<CommentsProps>(
  (props) => {
    const { comments, activeAsset, filter } = props;
    const classes = useCSS();
    return (
      <Scrollbar height={'100%'} data-pw="review-comments-list" className={classes.scrollbarContainer}>
        {comments?.length ? (
          comments.map((item, idx) => (
            <div key={item.id + idx} style={{ margin: '12px 0px', marginRight: '14px' }}>
              <ReviewComment comment={item} assetId={activeAsset?.id} />
            </div>
          ))
        ) : (
          <p style={{ margin: '12px 0' }}>
            <Typography>
              {filter === 'all'
                ? 'No resolved comments, no unresolved comments'
                : filter === 'resolve'
                  ? 'No resolved comments'
                  : 'No unresolved comments'}
            </Typography>
          </p>
        )}
      </Scrollbar>

    );
  },
  (prev, next) => {
    if (prev.activeAsset !== next.activeAsset) {
      return false;
    }

    if (prev.filter !== next.filter) {
      return false;
    }

    if (prev.comments !== next.comments && (!next.comments || !prev.comments)) {
      return false;
    }

    if (prev.comments === next.comments) {
      return true;
    }

    if (prev.comments.length !== next.comments.length) {
      return false;
    }

    if (prev.comments.some((c, idx) => c !== next.comments[idx])) {
      return false;
    }

    return true;
  }
);
